// src/pages/SmartAlertPage.jsx
import React, { useState, useEffect } from "react";
import TestLiveModeSwitch from "../components/TestLiveModeSwitch";
import StealthModeSwitch from "../components/StealthModeSwitch";
import HomeSwitch from "../components/HomeSwitch";
import LocationSwitch from "../components/LocationSwitch";
import SmartAlertButton from "../components/SmartAlertButton";
import SmartAlertSettings from "../components/SmartAlertSettings";
import SmartAlertFAQ from "../components/SmartAlertFAQ";

const TEST_COLOR = "#D97706";
const LIVE_COLOR = "#DC2626";


const safeStr = (value) => (value ?? "").toString().trim();

const blockedChannelsForContact = (contact = {}) => {
  if (Array.isArray(contact.blocked_channels)) {
    return contact.blocked_channels
      .map((channel) => safeStr(channel).toLowerCase())
      .filter(Boolean);
  }

  const channels = [];
  if (contact?.deny_info?.email_denied) channels.push("email");
  if (contact?.deny_info?.phone_denied) channels.push("sms");
  return channels;
};

const buildBlockedDeliveryNotice = (sendResult) => {
  const denied = Array.isArray(sendResult?.denied_contacts)
    ? sendResult.denied_contacts
    : [];

  if (denied.length === 0) {
    return null;
  }

  const details = [];
  let blockedDeliveryCount = 0;

  denied.forEach((contact) => {
    const channels = blockedChannelsForContact(contact);
    blockedDeliveryCount += channels.length;

    if (channels.length === 0) {
      return;
    }

    const name = safeStr(contact?.name);
    const email = safeStr(contact?.email);
    const phone = safeStr(contact?.phone);

    const contactLabel =
      name ||
      email ||
      phone ||
      "Smart Alert contact";

    const emailBlocked = channels.includes("email");
    const smsBlocked = channels.includes("sms");

    let deliveryText = "";

    if (emailBlocked && smsBlocked) {
      deliveryText = "Email and SMS blocked; this contact was skipped.";
    } else if (emailBlocked) {
      deliveryText = phone
        ? "Email blocked; SMS continued."
        : "Email blocked; this contact was skipped.";
    } else if (smsBlocked) {
      deliveryText = email
        ? "SMS blocked; email continued."
        : "SMS blocked; this contact was skipped.";
    }

    details.push({
      contactLabel,
      deliveryText,
    });
  });

  if (blockedDeliveryCount <= 0) {
    return null;
  }

  return {
    blockedDeliveryCount,
    details,
  };
};

const SmartAlertPage = () => {
  const [mode, setMode] = useState(() => {
    return localStorage.getItem("test_live_mode") === "live" ? "live" : "test";
  });

  const [color, setColor] = useState(() => {
    return localStorage.getItem("test_live_mode") === "live"
      ? LIVE_COLOR
      : TEST_COLOR;
  });

  const [showSettings, setShowSettings] = useState(false);
  const [deliveryNotice, setDeliveryNotice] = useState(null);

  const [stealthMode, setStealthMode] = useState(() => {
    return localStorage.getItem("stealth_mode") !== "false";
  });

  const [showFAQ, setShowFAQ] = useState(() => {
    const saved = localStorage.getItem("smart_alert_faq_open");
    return saved ? saved === "true" : false;
  });

  const [isSmallScreen, setIsSmallScreen] = useState(false);

  useEffect(() => {
    localStorage.setItem("stealth_mode", stealthMode.toString());
  }, [stealthMode]);

  useEffect(() => {
    localStorage.setItem("smart_alert_faq_open", showFAQ.toString());
  }, [showFAQ]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1024px)");
    const apply = () => setIsSmallScreen(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  const handleStealthToggle = () => setStealthMode((prev) => !prev);

  const handleFire = (sendResult) => {
    // The send endpoint returns contacts/channels that were already blocked by
    // the global Smart Alert deny list and therefore skipped before dispatch.
    // Good contacts/channels continue normally.
    setDeliveryNotice(buildBlockedDeliveryNotice(sendResult));
  };

  const handleModeToggle = ({ mode, color }) => {
    localStorage.setItem("test_live_mode", mode);
    setMode(mode);
    setColor(color);
  };

  const wrapperStyle = {
    ...styles.wrapper,
    ...(isSmallScreen ? styles.wrapperMobile : null),
  };

  const titleStyle = {
    ...styles.heading,
    ...(isSmallScreen ? styles.headingMobile : null),
  };

  const contentStyle = {
    ...styles.contentCard,
    ...(isSmallScreen ? styles.contentCardMobile : null),
  };

  const switchStackStyle = {
    ...styles.switchStack,
    ...(isSmallScreen ? styles.switchStackMobile : null),
  };

  const buttonContainerStyle = {
    ...styles.buttonContainer,
    ...(isSmallScreen ? styles.buttonContainerMobile : null),
  };

  const modeBadgeStyle = {
    ...styles.statusBadge,
    background: color,
    borderColor: color,
    color: "#FFFFFF",
    boxShadow: `0 8px 18px ${mode === "live" ? "rgba(220,38,38,0.18)" : "rgba(217,119,6,0.18)"}`,
  };

  return (
    <div style={wrapperStyle}>
      <section style={styles.header}>
        <h1 style={titleStyle}>Smart Alert</h1>
        <p style={styles.subheading}>
          Hold to send an alert. Keep it simple, fast, and ready when needed.
        </p>
      </section>

      <section style={contentStyle}>
        <div style={styles.topStatusRow}>
          <div style={modeBadgeStyle}>
            {mode === "live" ? "Live Mode" : "Test Mode"}
          </div>

          <div style={styles.infoPill}>
            Stealth: <strong>{stealthMode ? "On" : "Off"}</strong>
          </div>
        </div>

        <div style={buttonContainerStyle}>
          <div style={styles.buttonStage}>
            <SmartAlertButton
              mode={mode}
              onFire={handleFire}
              onSendStart={() => setDeliveryNotice(null)}
              stealthMode={stealthMode}
              color={color}
            />
          </div>

          {deliveryNotice && (
            <div
              style={styles.deliveryNotice}
              role="status"
              aria-live="polite"
            >
              <div style={styles.deliveryNoticeTitle}>
                Sent — {deliveryNotice.blockedDeliveryCount} blocked{" "}
                {deliveryNotice.blockedDeliveryCount === 1
                  ? "delivery was"
                  : "deliveries were"}{" "}
                skipped.
              </div>

              <div style={styles.deliveryNoticeList}>
                {deliveryNotice.details.map((item, index) => (
                  <div
                    key={`${item.contactLabel}-${index}`}
                    style={styles.deliveryNoticeItem}
                  >
                    <strong>{item.contactLabel}:</strong>{" "}
                    {item.deliveryText}
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={() => setShowSettings(true)}
                style={styles.deliveryNoticeAction}
              >
                View / Review Contact
              </button>
            </div>
          )}
        </div>

        <div style={switchStackStyle}>
          <div style={styles.switchCard}>
            <div style={styles.switchLabel}>Home Mode</div>
            <div style={styles.switchControl}>
              <HomeSwitch />
            </div>
          </div>

          <div style={styles.switchCard}>
            <div style={styles.switchLabel}>Test / Live Mode</div>
            <div style={styles.switchControl}>
              <TestLiveModeSwitch onToggle={handleModeToggle} />
            </div>
          </div>

          <div style={styles.switchCard}>
            <div style={styles.switchLabel}>Stealth Mode</div>
            <div style={styles.switchControl}>
              <StealthModeSwitch
                stealthMode={stealthMode}
                onToggle={handleStealthToggle}
              />
            </div>
          </div>

          <div style={styles.switchCard}>
            <div style={styles.switchLabel}>Location Sharing</div>
            <div style={styles.switchControl}>
              <LocationSwitch />
            </div>
          </div>
        </div>
      </section>

      <div style={styles.panelToggleWrap}>
        <button
          type="button"
          onClick={() => setShowSettings((prev) => !prev)}
          style={styles.panelToggle}
        >
          <span style={styles.panelToggleIcon}>{showSettings ? "−" : "+"}</span>
          <span>{showSettings ? "Hide Settings" : "Show Settings"}</span>
        </button>
      </div>

      {showSettings && (
        <section style={styles.expandPanel}>
          <SmartAlertSettings />
        </section>
      )}

      <div style={styles.panelToggleWrap}>
        <button
          type="button"
          onClick={() => setShowFAQ((prev) => !prev)}
          style={styles.panelToggle}
        >
          <span style={styles.panelToggleIcon}>{showFAQ ? "−" : "+"}</span>
          <span>{showFAQ ? "Hide FAQ" : "Show FAQ"}</span>
        </button>
      </div>

      {showFAQ && (
        <section style={styles.expandPanel}>
          <SmartAlertFAQ />
        </section>
      )}
    </div>
  );
};

const styles = {
  wrapper: {
    width: "100%",
    maxWidth: 1120,
    margin: "0 auto",
    padding: "18px 16px 44px",
    boxSizing: "border-box",
    fontFamily: "sans-serif",
    background: "#F5F7FA",
    overflowX: "hidden",
  },
  wrapperMobile: {
    padding: "14px 12px 34px",
  },

  header: {
    padding: "12px 0 16px",
    textAlign: "center",
  },
  heading: {
    margin: 0,
    color: "#0F172A",
    fontSize: 34,
    lineHeight: 1.08,
    fontWeight: 950,
    letterSpacing: "-0.9px",
  },
  headingMobile: {
    fontSize: 30,
    letterSpacing: "-0.65px",
  },
  subheading: {
    margin: "10px auto 0",
    maxWidth: 620,
    color: "#475569",
    fontSize: 14.5,
    lineHeight: 1.6,
    fontWeight: 650,
  },

  contentCard: {
    width: "100%",
    maxWidth: 860,
    margin: "0 auto",
    borderRadius: 22,
    border: "1px solid rgba(2,6,23,0.08)",
    background: "#FFFFFF",
    boxShadow: "0 14px 34px rgba(2,6,23,0.06)",
    padding: "20px 20px 22px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 18,
    boxSizing: "border-box",
  },
  contentCardMobile: {
    width: "100%",
    maxWidth: "100%",
    padding: "16px 12px 18px",
    borderRadius: 18,
    margin: "0 auto",
  },

  topStatusRow: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 10,
  },
  statusBadge: {
    minHeight: 34,
    padding: "8px 13px",
    borderRadius: 999,
    fontSize: 13,
    lineHeight: 1,
    fontWeight: 950,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid transparent",
    letterSpacing: "0.1px",
  },
  infoPill: {
    minHeight: 34,
    padding: "8px 12px",
    borderRadius: 999,
    background: "#F8FAFC",
    border: "1px solid rgba(2,6,23,0.08)",
    color: "#334155",
    fontSize: 13,
    lineHeight: 1,
    fontWeight: 750,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
  },

  buttonContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: 12,
    marginTop: 4,
    marginBottom: 4,
  },
  buttonContainerMobile: {
    marginTop: 2,
    marginBottom: 2,
  },
  buttonStage: {
    width: "min(300px, 100%)",
    maxWidth: "100%",
    minHeight: 300,
    borderRadius: 999,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    boxSizing: "border-box",
    background:
      "radial-gradient(circle at center, rgba(37,99,235,0.05) 0%, rgba(37,99,235,0.03) 45%, rgba(255,255,255,0) 72%)",
  },

  deliveryNotice: {
    width: "100%",
    maxWidth: 560,
    boxSizing: "border-box",
    borderRadius: 14,
    border: "1px solid rgba(217,119,6,0.28)",
    background: "#FFFBEB",
    color: "#92400E",
    padding: "11px 13px",
    display: "grid",
    gap: 8,
    boxShadow: "0 8px 20px rgba(2,6,23,0.045)",
  },
  deliveryNoticeTitle: {
    fontSize: 13.5,
    lineHeight: 1.4,
    fontWeight: 950,
  },
  deliveryNoticeList: {
    display: "grid",
    gap: 5,
  },
  deliveryNoticeItem: {
    fontSize: 12.8,
    lineHeight: 1.45,
    fontWeight: 700,
  },
  deliveryNoticeAction: {
    width: "fit-content",
    appearance: "none",
    border: "1px solid rgba(30,64,175,0.20)",
    background: "#FFFFFF",
    color: "#1E40AF",
    borderRadius: 10,
    padding: "8px 11px",
    fontSize: 12.5,
    fontWeight: 900,
    cursor: "pointer",
  },

  switchStack: {
    width: "100%",
    maxWidth: 720,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 14,
    marginTop: 4,
  },
  switchStackMobile: {
    gridTemplateColumns: "1fr",
    maxWidth: "100%",
  },
  switchCard: {
    borderRadius: 16,
    border: "1px solid rgba(2,6,23,0.08)",
    background: "#F8FAFC",
    padding: "14px 14px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    minHeight: 72,
    boxSizing: "border-box",
  },
  switchLabel: {
    color: "#0F172A",
    fontSize: 14,
    lineHeight: 1.35,
    fontWeight: 900,
    letterSpacing: "-0.15px",
  },
  switchControl: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    minWidth: 96,
  },

  panelToggleWrap: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 14,
  },
  panelToggle: {
    appearance: "none",
    border: "1px solid rgba(2,6,23,0.08)",
    background: "#FFFFFF",
    color: "#0F172A",
    borderRadius: 14,
    padding: "12px 16px",
    fontSize: 14,
    fontWeight: 900,
    lineHeight: 1,
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    boxShadow: "0 10px 24px rgba(2,6,23,0.05)",
  },
  panelToggleIcon: {
    width: 22,
    height: 22,
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(37,99,235,0.10)",
    color: "#2563EB",
    fontSize: 15,
    fontWeight: 950,
    lineHeight: 1,
  },

  expandPanel: {
    marginTop: 12,
    borderRadius: 18,
    border: "1px solid rgba(2,6,23,0.08)",
    background: "#FFFFFF",
    boxShadow: "0 12px 30px rgba(2,6,23,0.055)",
    padding: 16,
  },
};

export default SmartAlertPage;
